import React from 'react';
import { X, ExternalLink, Smartphone } from 'lucide-react';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ParticipantModePanel.module.css';
import joinStyles from '../join/JoinPage.module.css';

/**
 * ParticipantModePanel
 * Renders the same look as JoinPage (joinRoot + phoneFrame + page) when embedded with children.
 *
 * Props:
 *  - isOpen       : boolean — whether the panel is visible
 *  - onClose      : fn     — close the panel
 *  - embedded     : boolean — if true, render inline in layout (e.g. right panel) instead of fixed overlay
 *  - eventName    : string — shown in the phone header
 *  - activityType : string — e.g. "Multiple Choice", "Q&A"
 *  - children     : ReactNode — JoinPagePreview (same structure as JoinPage content)
 */
const ParticipantModePanel = ({ isOpen, onClose, embedded = false, eventName = 'My Event', eventCode, activityType, children }) => {
    if (!isOpen) return null;

    const useJoinPageLook = embedded && children;

    return (
        <div className={`${styles.panel} ${embedded ? styles.panelEmbedded : ''}`}>
            {/* Compact header */}
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderLeft}>
                    <Smartphone size={14} className={styles.panelHeaderIcon} />
                    <span className={styles.panelTitle}>Participant preview</span>
                </div>
                <div className={styles.panelHeaderActions}>
                    <Tooltip title="Open in new tab" placement="bottom">
                        <button
                            type="button"
                            className={styles.panelActionBtn}
                            onClick={() => eventCode && window.open(`${window.location.origin}/join/${eventCode}`, '_blank')}
                            aria-label="Open participant view in new tab"
                        >
                            <ExternalLink size={14} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Close" placement="bottom">
                        <button type="button" className={styles.panelActionBtn} onClick={onClose} aria-label="Close participant preview">
                            <X size={14} />
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Same structure as JoinPage: joinRoot > phoneFrame > (page content) */}
            {useJoinPageLook ? (
                <div className={joinStyles.joinRootEmbedded}>
                    <div className={joinStyles.phoneFrameEmbedded}>
                        {children}
                    </div>
                </div>
            ) : (
                <div className={styles.phoneWrap}>
                    <div className={styles.phone}>
                        <div className={styles.phoneBezel}>
                            <div className={styles.phoneNotch} aria-hidden />
                        </div>
                        <div className={styles.phoneScreen}>
                            <div className={styles.participantContent}>
                                {children || (
                                    <div className={styles.emptyPreview}>
                                        <Smartphone size={28} className={styles.emptyIllustration} aria-hidden />
                                        <span className={styles.emptyText}>Preview will appear here</span>
                                    </div>
                                )}
                            </div>
                            <div className={styles.phoneHomeBar} aria-hidden />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParticipantModePanel;
